/**
 * Two flavors of confirmation dialog used across the furniture pages:
 *  - <ConfirmDeleteDialog>: simple yes/no with strong wording. Used for items.
 *  - <TypedConfirmDeleteDialog>: requires typing a phrase to enable the
 *    destructive button. Used for projects (where there's no undo).
 *
 * Kept tiny on purpose — these are leaf components with no business logic.
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle } from 'lucide-react';

export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title = 'Delete?',
  description,
  destructiveLabel = 'Delete',
  cancelLabel = 'Cancel',
  onConfirm,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="confirm-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#a8324a]">
            <AlertTriangle className="h-4 w-4" /> {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="confirm-delete-cancel">{cancelLabel}</Button>
          <Button
            variant="destructive"
            onClick={() => { onOpenChange(false); onConfirm?.(); }}
            data-testid="confirm-delete-confirm"
          >
            {destructiveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TypedConfirmDeleteDialog({
  open,
  onOpenChange,
  title = 'Delete forever?',
  description,
  // Phrase the user must type exactly to enable the destructive button.
  phrase = 'DELETE',
  destructiveLabel = 'Delete forever',
  onConfirm,
}) {
  const [value, setValue] = useState('');
  useEffect(() => { if (!open) setValue(''); }, [open]);
  const ok = value.trim() === phrase;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="typed-confirm-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#a8324a]">
            <AlertTriangle className="h-4 w-4" /> {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="py-2 space-y-2">
          <p className="text-sm text-[#5b6773]">
            Type <span className="font-mono font-semibold text-[#1f2933]">{phrase}</span> to confirm.
          </p>
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={phrase}
            data-testid="typed-confirm-input"
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="typed-confirm-cancel">Cancel</Button>
          <Button
            variant="destructive"
            disabled={!ok}
            onClick={() => { onOpenChange(false); onConfirm?.(); }}
            data-testid="typed-confirm-confirm"
          >
            {destructiveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
